import { useEffect, useState } from "react";
import api from "../lib/api";
import { Languages, ArrowRight, Loader2, Copy, Globe2 } from "lucide-react";
import { toast } from "sonner";

const CONTEXTS = [
  { v: "crm", l: "CRM Message" },
  { v: "form", l: "Open Form Office" },
  { v: "intake", l: "Customer Intake" },
  { v: "legal", l: "Legal / Contract" },
  { v: "general", l: "General Business" },
];

export default function TranslateHub() {
  const [langs, setLangs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [text, setText] = useState("");
  const [source, setSource] = useState("auto");
  const [target, setTarget] = useState("es");
  const [context, setContext] = useState("crm");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/languages").then(r => setLangs(r.data));
    api.get("/countries").then(r => setCountries(r.data));
  }, []);

  const translate = async (e) => {
    e?.preventDefault();
    if (!text.trim()) { toast.error("Add text first."); return; }
    setLoading(true); setResult("");
    try {
      const { data } = await api.post("/translate", { text, source_lang: source, target_lang: target, context });
      setResult(data.translated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Translation failed");
    } finally { setLoading(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(result);
    toast.success("Copied to clipboard");
  };

  const regions = [...new Set(countries.map(c => c.region))];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="translate-hub">
      <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// Worldwide Operations</div>
      <h1 className="display-black text-5xl lg:text-6xl uppercase text-[#064E3B] mt-3 tracking-tighter max-w-4xl">
        Translation Hub.
      </h1>
      <p className="text-gray-700 mt-4 max-w-2xl">
        AI translation for CRM messaging, intake forms, Open Form Office, and legal docs. {langs.length} languages — {countries.length} target markets.
      </p>

      {/* Translation Form */}
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-px bg-gray-300 border border-gray-300" data-testid="translate-form">
        <div className="bg-white p-8">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <Languages className="w-5 h-5 text-[#D97706]" />
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#064E3B]">Source</div>
            </div>
            <select className="input-federal max-w-[200px]" value={source} onChange={e => setSource(e.target.value)} data-testid="translate-source">
              <option value="auto">Auto-detect</option>
              {langs.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </div>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="Type or paste your text…"
            rows={10}
            className="input-federal w-full"
            data-testid="translate-input"
          />
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <select className="input-federal max-w-[260px]" value={context} onChange={e => setContext(e.target.value)} data-testid="translate-context">
              {CONTEXTS.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
            <button onClick={translate} disabled={loading} className="btn-federal" data-testid="translate-submit">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Translating…</> : <>Translate <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
        </div>

        <div className="bg-gray-50 p-8">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <Languages className="w-5 h-5 text-[#D97706]" />
              <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#064E3B]">Target</div>
            </div>
            <select className="input-federal max-w-[200px]" value={target} onChange={e => setTarget(e.target.value)} data-testid="translate-target">
              {langs.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </div>
          <div className="border border-gray-300 bg-white p-4 min-h-[260px] text-sm text-gray-800 whitespace-pre-wrap" data-testid="translate-output">
            {result || <span className="text-gray-400">Translation appears here…</span>}
          </div>
          {result && (
            <button onClick={copy} className="btn-ghost mt-4" data-testid="translate-copy">
              <Copy className="w-4 h-4" /> Copy
            </button>
          )}
        </div>
      </div>

      {/* Worldwide markets */}
      <section className="mt-20" data-testid="worldwide-markets">
        <div className="flex items-center gap-3">
          <Globe2 className="w-5 h-5 text-[#D97706]" />
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#D97706]">// Worldwide</div>
        </div>
        <h2 className="display-black text-3xl lg:text-4xl uppercase text-[#064E3B] mt-3">
          Active or expanding in {countries.length} countries.
        </h2>
        <p className="text-gray-700 mt-2 max-w-2xl">North America active. Europe, Asia, Middle East, Oceania, South America, Africa — expanding.</p>

        <div className="mt-8 space-y-10">
          {regions.map(r => {
            const items = countries.filter(c => c.region === r);
            return (
              <div key={r} data-testid={`region-${r.toLowerCase().replace(/\s/g, "-")}`}>
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#064E3B] mb-3">// {r}</div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-px bg-gray-300 border border-gray-300">
                  {items.map(c => (
                    <div key={c.code} className={`p-5 ${c.status === "active" ? "bg-[#064E3B] text-white" : "bg-white"}`}>
                      <div className="font-bold text-sm">{c.name}</div>
                      <div className={`mono text-[10px] tracking-wider uppercase mt-1 ${c.status === "active" ? "text-[#FACC15]" : "text-[#D97706]"}`}>
                        {c.status === "active" ? "★ Active" : "Expanding"}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
